import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { useEventId } from "../EventContext";
import { useAuth, Button, InputField } from "@vionixasia/shared-components";

/**
 * The event's log: what officers typed (manual_entries) and what the decoder
 * read (raw_passings), kept 90 days. Fly's log lines are gone within hours, so
 * this is where race-day questions are answered — "did the water station send
 * BIB 6000?", "did his chip ever hit the mat?".
 *
 * The officer tab opens on one line per phone. The server cannot know where an
 * officer is standing; it stores whatever location their phone has selected.
 * A phone sending to two locations, or a checkpoint phone left on Start/Finish
 * (the input page's default), is a wrong pick, and the earlier it is caught the
 * fewer results it touches — a CP entry on Start/Finish becomes a finish, and
 * the mat's real finish later loses to it.
 */

const FormField = forwardRef(
  ({ className = "", wrapperClassName = "", ...props }, ref) => (
    <InputField ref={ref} labelColor="text-slate-700" className={className} wrapperClassName={wrapperClassName} {...props} />
  ),
);

const API = import.meta.env.VITE_API_BASE_URL;
const REFRESH_MS = 20000;

const timeOf = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const t = d.toLocaleTimeString("en-GB", { hour12: false });
  return d.toDateString() === new Date().toDateString()
    ? t
    : `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} ${t}`;
};

const OUTCOME = {
  counted: { label: "Counted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pre_start: { label: "Before start", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  not_counted: { label: "Not counted", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  duplicate: { label: "Duplicate", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  not_changed: { label: "Not changed", cls: "bg-slate-50 text-slate-600 border-slate-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
  rate_limited: { label: "Rate limited", cls: "bg-red-50 text-red-700 border-red-200" },
  error: { label: "Error", cls: "bg-red-50 text-red-700 border-red-200" },
};
const Outcome = ({ value }) => {
  const o = OUTCOME[value] || { label: value || "-", cls: "bg-slate-50 text-slate-600 border-slate-200" };
  return <span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${o.cls}`}>{o.label}</span>;
};

const TH = "bg-slate-50 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200";
const TD = "bg-white px-4 py-3 text-sm border-y border-slate-200";
const first = " rounded-l-card border-l";
const last = " rounded-r-card border-r";

const SOURCE = { "record-time": "Auto-sync", "record-time-batch": "Sync", "record-time-loop": "Loop", "record-time-loop-batch": "Loop sync", "recover-missed-loop-opening": "Loop repair" };

export const EventLog = () => {
  const { realEventId: eventId } = useEventId();
  const { getIdToken } = useAuth();

  const [tab, setTab] = useState("officers");
  const [event, setEvent] = useState(null);
  const [names, setNames] = useState(() => new Map());
  // One page from the server: never the whole event in the browser.
  const [log, setLog] = useState({ all: 0, outcomes: {}, phones: [], total: 0, page: 1, pages: 1, entries: [] });
  const [page, setPage] = useState(1);
  const [decoder, setDecoder] = useState({ total: 0, passings: [], more: false });
  // Decoder pages go back in time: the receivedAt each older page starts before.
  const [cursors, setCursors] = useState([]);
  const [bib, setBib] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [outcome, setOutcome] = useState("");
  const [auto, setAuto] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const authenticatedFetch = useCallback(
    async (url) => {
      const token = await getIdToken();
      return fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    },
    [getIdToken],
  );

  useEffect(() => {
    if (!eventId) return;
    fetch(`${API}/api/timing/event_timing`)
      .then((r) => r.json())
      .then((all) => setEvent((Array.isArray(all) ? all : []).find((e) => e.id === eventId || e.eventId === eventId) || null))
      .catch(() => {});
    fetch(`${API}/api/timing/participants?eventId=${encodeURIComponent(eventId)}&limit=5000`)
      .then((r) => r.json())
      .then((d) => setNames(new Map((d?.participants || d || []).map((p) => [String(p.bib), p.name]))))
      .catch(() => {});
  }, [eventId]);

  // locationId → the name officers see on the input page.
  const locationName = useMemo(() => {
    const m = new Map();
    (event?.categories || []).forEach((c) => (c.checkpoints || []).forEach((cp) => cp.locationId && !m.has(cp.locationId) && m.set(cp.locationId, cp.name)));
    return m;
  }, [event]);
  const isMat = useCallback((locationId) => {
    for (const c of event?.categories || []) {
      const cp = (c.checkpoints || []).find((x) => x.locationId === locationId);
      if (cp) return ["startfinish", "start", "finish"].includes(String(cp.type || "").toLowerCase());
    }
    return locationId === "start/finish";
  }, [event]);
  const placeOf = (e) => (e.checkpointId ? locationName.get(e.checkpointId) || e.checkpointId : e.loopNumber != null ? `Loop ${e.loopNumber}` : "-");

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({ eventId });
      if (bib.trim()) qs.set("bib", bib.trim());
      if (tab === "officers") {
        if (location) qs.set("location", location);
        if (outcome) qs.set("outcome", outcome);
        if (phone) qs.set("phone", phone);
        qs.set("page", String(page));
        const r = await authenticatedFetch(`${API}/api/timing/input-log?${qs}`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.message || d.error || `HTTP ${r.status}`);
        setLog(d);
      } else {
        qs.set("limit", "50");
        if (cursors.length) qs.set("before", cursors[cursors.length - 1]);
        const r = await authenticatedFetch(`${API}/api/timing/decoder-log?${qs}`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.message || d.error || `HTTP ${r.status}`);
        setDecoder({ total: d.total || 0, passings: d.passings || [], more: !!d.more });
      }
      setUpdatedAt(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId, tab, bib, location, outcome, phone, page, cursors, authenticatedFetch]);

  // A new filter or page loads at once; typing a BIB waits for Search.
  useEffect(() => { load(); }, [eventId, tab, location, outcome, phone, page, cursors]); // eslint-disable-line react-hooks/exhaustive-deps
  // A filter starts again from the newest.
  useEffect(() => { setPage(1); }, [location, outcome, phone]);
  // Refresh only the newest page, so reading an older one is not pulled away.
  const onNewest = tab === "officers" ? page === 1 : cursors.length === 0;
  useEffect(() => {
    if (!auto || !onNewest) return undefined;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [auto, onNewest, load]);
  const search = () => { setPage(1); setCursors([]); load(); };

  const WARN = {
    mat_and_checkpoint: "Sends to Start/Finish and a checkpoint — a CP phone on Start/Finish turns its entries into finishes",
    several_locations: "Sends to more than one location",
  };

  // Newer / older, and where this page sits.
  const Pager = ({ label, canNewer, canOlder, onNewer, onOlder, onNewest }) => (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm tabular-nums text-slate-500">{label}</span>
      <div className="flex gap-2">
        {onNewest && <Button variant="secondary" color="slate" onClick={onNewest} disabled={!canNewer}>Newest</Button>}
        <Button variant="secondary" color="slate" onClick={onNewer} disabled={!canNewer}>← Newer</Button>
        <Button variant="secondary" color="slate" onClick={onOlder} disabled={!canOlder}>Older →</Button>
      </div>
    </div>
  );

  // The same switch as the category tabs on the results page.
  const tabBtn = (id, label) => (
    <Button key={id} onClick={() => setTab(id)} variant={tab === id ? "primary" : "secondary"} color="slate">
      {label}
    </Button>
  );

  return (
    <section className="min-h-screen mt-10 bg-white mb-10">
      <div className="container mx-auto px-4">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 font-oxanium">Log</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every entry officers typed and every chip the decoder read, kept 90 days — counted or not.
          </p>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          {tabBtn("officers", "Officer input")}
          {tabBtn("decoder", "Decoder (MYLAPS)")}
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <form className="flex w-full items-end gap-2 sm:max-w-md" onSubmit={(e) => { e.preventDefault(); search(); }}>
            <FormField label="BIB" name="log-bib" labelClassName="sr-only" value={bib} onChange={(e) => setBib(e.target.value)}
              placeholder="Search BIB (empty: all)" wrapperClassName="w-full" inputMode="numeric" />
            <Button type="submit" color="cyan" disabled={loading}>{loading ? "Loading…" : "Search"}</Button>
          </form>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="h-4 w-4 accent-cyan-600" />
              Auto refresh
            </label>
            <span className="tabular-nums">{updatedAt ? `Updated ${timeOf(updatedAt.toISOString())}` : ""}</span>
          </div>
        </div>

        {error && <p className="mb-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        {tab === "officers" ? (
          <>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-500">Phones</h2>
            <p className="mb-3 text-sm text-slate-500">
              One line per phone (by IP; phones on one mobile network can share an IP, so a warning is a reason to call the officer, not proof). Click a line to show its entries.
            </p>
            <div className="mb-8 overflow-x-auto">
              <table className="min-w-full border-separate table-auto" style={{ borderSpacing: "0 6px" }}>
                <thead>
                  <tr>
                    <th className={TH + first}>Phone</th>
                    <th className={TH}>Location</th>
                    <th className={TH + " text-right"}>Entries</th>
                    <th className={TH}>First – last</th>
                    <th className={TH + last}>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {log.phones.length === 0 ? (
                    <tr><td colSpan={5} className={TD + first + last + " text-center text-slate-500"}>{loading ? "Loading…" : "No entries yet"}</td></tr>
                  ) : log.phones.map((p) => (
                    <tr key={p.key} onClick={() => setPhone(phone === p.key ? "" : p.key)}
                      className={`cursor-pointer ${phone === p.key ? "[&>td]:bg-cyan-50" : p.warn ? "[&>td]:bg-amber-50" : "[&>td]:hover:bg-slate-50"}`}>
                      <td className={TD + first + " font-mono text-xs text-slate-700"}>{p.ip || "-"}{p.uid ? <div className="text-slate-400">{p.uid.slice(0, 8)}</div> : null}</td>
                      <td className={TD}>
                        {Object.entries(p.places).map(([pl, cnt]) => (
                          <div key={pl} className={isMat(pl) && p.warn ? "font-semibold text-amber-800" : "text-slate-800"}>
                            {pl === "loop" ? "Loop" : locationName.get(pl) || pl} <span className="text-slate-400">({cnt})</span>
                          </div>
                        ))}
                        {p.warn && <div className="mt-1 text-xs font-semibold text-amber-700">⚠ {WARN[p.warn] || p.warn}</div>}
                      </td>
                      <td className={TD + " text-right tabular-nums font-semibold"}>{p.count}</td>
                      <td className={TD + " whitespace-nowrap tabular-nums text-slate-600"}>{timeOf(p.firstAt)} – {timeOf(p.lastAt)}</td>
                      <td className={TD + last}>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(p.outcomes).map(([o, n]) => <span key={o} className="flex items-center gap-1"><Outcome value={o} /><span className="text-xs text-slate-500">{n}</span></span>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h2 className="mr-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
                Entries <span className="normal-case text-slate-400">({log.total.toLocaleString()} of {log.all.toLocaleString()})</span>
              </h2>
              <FormField label="Location" name="log-location" labelClassName="sr-only" type="select" value={location}
                onChange={(e) => setLocation(e.target.value)} defaultOptionLabel="All locations" wrapperClassName="w-full sm:w-56"
                options={[...locationName.entries()].map(([id, n]) => ({ value: id, label: n }))} />
              <FormField label="Outcome" name="log-outcome" labelClassName="sr-only" type="select" value={outcome}
                onChange={(e) => setOutcome(e.target.value)} defaultOptionLabel="All outcomes" wrapperClassName="w-full sm:w-56"
                options={Object.entries(log.outcomes).map(([o, n]) => ({ value: o, label: `${OUTCOME[o]?.label || o} (${n})` }))} />
              {phone && <Button variant="ghost" color="cyan" onClick={() => setPhone("")}>Show all phones</Button>}
            </div>
            <div className="overflow-x-auto pb-8">
              <table className="min-w-full border-separate table-auto" style={{ borderSpacing: "0 6px" }}>
                <thead>
                  <tr>
                    <th className={TH + first}>Received</th>
                    <th className={TH}>BIB</th>
                    <th className={TH}>Location</th>
                    <th className={TH}>Entry time</th>
                    <th className={TH}>Outcome</th>
                    <th className={TH}>Sent by</th>
                    <th className={TH + last}>Server answer</th>
                  </tr>
                </thead>
                <tbody>
                  {log.entries.length === 0 && (
                    <tr><td colSpan={7} className={TD + first + last + " text-center text-slate-500"}>{loading ? "Loading…" : "No entries"}</td></tr>
                  )}
                  {log.entries.map((e) => (
                    <tr key={e.id}>
                      <td className={TD + first + " whitespace-nowrap tabular-nums text-slate-600"}>{timeOf(e.receivedAt)}</td>
                      <td className={TD}>
                        <span className="font-semibold tabular-nums text-slate-900">{e.bib}</span>
                        <div className="max-w-[12rem] truncate text-xs uppercase text-slate-500">{names.get(String(e.bib)) || ""}</div>
                      </td>
                      <td className={TD + " text-slate-800"}>{placeOf(e)}</td>
                      <td className={TD + " whitespace-nowrap tabular-nums text-slate-800"}>
                        {timeOf(e.timestamp)}
                        {e.forceUpdate && <div className="text-xs text-slate-500">edit of {timeOf(e.originalTime)}</div>}
                      </td>
                      <td className={TD}><Outcome value={e.outcome} /></td>
                      <td className={TD + " whitespace-nowrap text-xs text-slate-600"}>
                        <div>{SOURCE[e.source] || e.source}</div>
                        <div className="font-mono text-slate-400">{e.ip || "-"}{e.admin ? " · admin" : ""}</div>
                      </td>
                      <td className={TD + last + " text-xs text-slate-600"}>{e.message || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {log.total > 0 && (
                <Pager
                  label={`${((log.page - 1) * log.pageSize + 1).toLocaleString()}–${Math.min(log.page * log.pageSize, log.total).toLocaleString()} of ${log.total.toLocaleString()} · page ${log.page} of ${log.pages}`}
                  canNewer={log.page > 1} canOlder={log.page < log.pages}
                  onNewer={() => setPage(log.page - 1)} onOlder={() => setPage(log.page + 1)} onNewest={() => setPage(1)}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <p className="mb-3 text-sm text-slate-500">
              {bib.trim() ? `Readings of BIB ${bib.trim()}` : `${decoder.total.toLocaleString()} readings`}, newest first — each crossing as the mat sent it, before any timing rule. A chip is read several times per crossing.
            </p>
            <div className="overflow-x-auto pb-8">
              <table className="min-w-full border-separate table-auto" style={{ borderSpacing: "0 6px" }}>
                <thead>
                  <tr>
                    <th className={TH + first}>Received</th>
                    <th className={TH}>Crossing time</th>
                    <th className={TH}>BIB</th>
                    <th className={TH}>Chip</th>
                    <th className={TH}>Location</th>
                    <th className={TH + last}>Decoder</th>
                  </tr>
                </thead>
                <tbody>
                  {decoder.passings.length === 0 ? (
                    <tr><td colSpan={6} className={TD + first + last + " text-center text-slate-500"}>{loading ? "Loading…" : "No readings"}</td></tr>
                  ) : decoder.passings.map((p) => (
                    <tr key={p.id} className={p.resolveSource === "unmapped" ? "[&>td]:bg-amber-50" : ""}>
                      <td className={TD + first + " whitespace-nowrap tabular-nums text-slate-600"}>{timeOf(p.receivedAt)}</td>
                      <td className={TD + " whitespace-nowrap tabular-nums font-semibold text-slate-900"}>{timeOf(p.passingTime)}</td>
                      <td className={TD}>
                        {p.bib ? <span className="font-semibold tabular-nums text-slate-900">{p.bib}</span> : <span className="text-xs font-semibold text-amber-700">⚠ chip not mapped to a BIB</span>}
                        <div className="max-w-[12rem] truncate text-xs uppercase text-slate-500">{names.get(String(p.bib)) || ""}</div>
                      </td>
                      <td className={TD + " font-mono text-xs text-slate-700"}>{p.chip}</td>
                      <td className={TD + " text-slate-800"}>{p.location || "-"}</td>
                      <td className={TD + last + " font-mono text-xs text-slate-500"}>{p.deviceMac || "-"}{p.timeSource ? ` · ${p.timeSource}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {decoder.passings.length > 0 && (
                <Pager
                  label={`${decoder.passings.length} readings · ${timeOf(decoder.passings[0].receivedAt)} – ${timeOf(decoder.passings[decoder.passings.length - 1].receivedAt)}`}
                  canNewer={cursors.length > 0} canOlder={decoder.more}
                  onNewer={() => setCursors((c) => c.slice(0, -1))}
                  onOlder={() => setCursors((c) => [...c, decoder.passings[decoder.passings.length - 1].receivedAt])}
                  onNewest={() => setCursors([])}
                />
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};
